import { css } from "@emotion/css";
import { useMemo } from "react";

import { Divider } from "../divider/Divider";
import { Icon } from "../../foundations/icons/Icon/Icon";
import { Text } from "../../foundations/typography/Text";
import { useColors } from "../../hooks/useColors";

import { StepType } from "./ActivityStepper";

interface StepItemProps {
  step: StepType;
  index: number;
  isCurrentStep: boolean;
  isLastStep: boolean;
  isFirstStep: boolean;
}
interface StepperProps {
  steps: StepType[];
  containerWidth?: string;
  hide?: boolean;
}
export function ProgressStepperHorizontal(
  props: StepperProps & { currentStepIndex: number }
) {
  // first incomplete step marks the current step
  const firstIncompleteStep = useMemo(
    () => props.steps.findIndex((step) => step.status === "INCOMPLETE"),
    [props.steps]
  );

  const {
    steps,
    currentStepIndex = firstIncompleteStep,
    containerWidth,
    hide,
  } = props;

  return (
    <div
      className={css(styles.stepsContainer, {
        width: containerWidth ? containerWidth : "796px",
        display: hide ? "none" : "flex",
      })}
    >
      {steps.map((step, index: number) => (
        <StepItem
          key={index}
          step={step}
          index={index}
          isCurrentStep={currentStepIndex === index}
          isLastStep={steps.length - 1 === index}
          isFirstStep={index === 0}
        />
      ))}
    </div>
  );
}

const StepItem = ({
  step,
  index,
  isCurrentStep,
  isLastStep,
  isFirstStep,
}: StepItemProps) => {
  const isComplete = step.status === "COMPLETE";
  const stepLabel = (index + 1).toString();
  const colors = useColors();
  let backgroundColor = "var(--background-selected-weak-hover)";

  if (!isComplete && isLastStep) backgroundColor = "var(--surface-base)";

  if (isCurrentStep)
    backgroundColor = "var(--background-accent-neutral-strong)";

  if (isComplete && isLastStep)
    backgroundColor = "var(--background-accent-green-strong)";

  return (
    <div
      className={css({
        flex: 1,
      })}
      key={index}
    >
      <div className={styles.stepContainer}>
        <div className={styles.iconAndDividerContainer}>
          <div className={styles.divider}>
            <Divider
              className={css({
                visibility: isFirstStep ? "hidden" : undefined,
              })}
            />
          </div>
          <div
            className={css(
              styles.icon,
              { backgroundColor },
              !isComplete &&
                isLastStep && {
                  boxShadow: `inset 0 0 0 1px ${colors["border-base"]}`,
                }
            )}
          >
            {isComplete ? (
              <Icon
                name="check"
                type="mini"
                color={isComplete && !isLastStep ? "icon-base" : "icon-inverse"}
              />
            ) : (
              <Text
                color={isCurrentStep ? "text-inverse" : "text-weak"}
                style="global/label-small"
              >
                {stepLabel}
              </Text>
            )}
          </div>
          <div className={styles.divider}>
            <Divider
              className={css({
                visibility: isLastStep ? "hidden" : undefined,
              })}
            />
          </div>
        </div>
        <div className={css({ alignSelf: "center" })}>
          <Text
            className={css({ textAlign: "center" })}
            color="text-base"
            style="global/heading-small"
          >
            {step.title}
          </Text>
        </div>
        {step.description && (
          <div className={styles.description}>
            <Text
              className={css({
                visibility: isCurrentStep || isComplete ? undefined : "hidden",
                textAlign: "center",
                alignSelf: "stretch",
              })}
              color="text-placeholder"
              style="form/body-secondary"
            >
              {step.description}
            </Text>
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  stepsContainer: css({
    alignItems: "flex-start",
    gap: "var(--space-0)",
    padding: "var(--space-0)",
  }),
  stepContainer: css({
    flex: 1,
    flexBasis: 0,
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-2)",
    alignItems: "flex-start",
    paddingLeft: "var(--space-0)",
    paddingRight: "var(--space-0)",
    paddingTop: "5px",
    paddingBottom: "5px",
  }),
  iconAndDividerContainer: css({
    display: "flex",
    justifyContent: "center",
    gap: "var(--space-3)",
    padding: "var(--space-0)",
    alignItems: "center",
    alignSelf: "stretch",
  }),
  divider: css({
    flexGrow: 1,
  }),
  icon: css({
    display: "flex",
    height: "var(--space-6)",
    width: "var(--space-6)",
    borderRadius: "50%",
    justifyContent: "center",
    alignItems: "center",
  }),
  description: css({
    display: "flex",
    paddingTop: "var(--space-0)",
    paddingBottom: "var(--space-0)",
    paddingLeft: "var(--space-1-5)",
    paddingRight: "var(--space-1-5)",
    flexDirection: "column",
    alignItems: "center",
    gap: "var(--space-0)",
    alignSelf: "stretch",
  }),
};
